/**
 * Newsletter sign-up without the page reload + scroll jump (task 86aj16ree).
 *
 * The native {% form 'customer' %} posts to /contact#contact_form, so Shopify
 * reloads the page (redirecting to /?customer_posted=true#contact_form) and the
 * fragment scrolls the viewport down to the footer. This takes over the submit,
 * posts the same data via fetch, and shows the success/error message in place.
 *
 * Success is detected from the followed redirect URL (customer_posted=true) —
 * the canonical Shopify signal — not by scraping a specific element, so it
 * never falls back to a reload on the happy path. A true network failure is the
 * only case that defers to a native submit. Progressive enhancement: with JS
 * off, the form still works the old way.
 */
class EmailSignupForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (this.form) this.form.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = async (event) => {
    // Take over the submit immediately so the page never reloads/scrolls.
    event.preventDefault();

    const form = this.form;
    const button = form.querySelector('button');
    if (button) button.disabled = true;

    try {
      const response = await fetch(form.action.split('#')[0], {
        method: 'POST',
        body: new URLSearchParams(new FormData(form)),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });

      const succeeded = response.url.includes('customer_posted=true');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      this.#clearMessages();

      if (succeeded) {
        form.appendChild(this.#successMessage(doc));
        const input = form.querySelector('input[type="email"]');
        if (input) input.value = '';
        return;
      }

      const error =
        doc.querySelector('[id^="Email-signup__message-error-"]') ||
        doc.querySelector('.email-signup__message');
      if (error) {
        form.appendChild(error);
        return;
      }

      // No recognizable outcome (e.g. a bot-protection challenge) — let the
      // browser submit natively so Shopify can run its own flow.
      form.submit();
    } catch {
      form.submit(); // network error — fall back to a native submit
    } finally {
      if (button) button.disabled = false;
    }
  };

  #clearMessages() {
    this.form.querySelectorAll('.email-signup__message').forEach((node) => node.remove());
  }

  #successMessage(doc) {
    // Prefer the message Shopify rendered (it carries the check icon + copy);
    // fall back to the localized text passed via data-success-text.
    const rendered = doc.querySelector('[id^="Email-signup__message-success-"]');
    if (rendered) return rendered;

    const wrapper = document.createElement('div');
    wrapper.className = 'email-signup__message';
    const text = document.createElement('p');
    text.className = 'email-signup__message-text';
    text.textContent = this.dataset.successText || '';
    wrapper.appendChild(text);
    return wrapper;
  }
}

if (!customElements.get('email-signup-form')) {
  customElements.define('email-signup-form', EmailSignupForm);
}
